/**
 * Trade-in-specific detail sections: key-facts strip, Supplier / Origin /
 * Payout / Processing rows, and trade-in-level flag chips. The label/value
 * rows are react-admin <SimpleShowLayout> blocks reading from the trade-in
 * record context via `source` paths; composite/derived values use
 * <FunctionField>. Key facts and flag chips stay on the shared kit's bespoke
 * primitives. The field groupings follow reware-admin's trade-in detail
 * (Tradeins/TradeinInfo.jsx, Tradeins/GiftcardDetails.jsx, Orders/CustomerDetails.jsx,
 * Orders/Address.jsx) without reproducing their full field set — see TradeinExpand.
 */
import { FunctionField, SimpleShowLayout, TextField } from 'react-admin';
import { Box } from '@mui/material';

import {
  detailLayoutSx,
  displayNameFor,
  type Flag,
  FlagChips,
  formatDate,
  formatDateTime,
  type KeyFact,
  KeyFactsStrip,
  monoFieldSx,
  SectionLabel,
} from '@src/components/indexPage';

import { formatMoney } from './format';
import { present, type ConstantOption, type TradeinRecord } from './types';

/**
 * Trade-in type display. reware-api sends the display-cased TRADEIN_SOURCES *value*, so the wire
 * carries 'MiTi' / 'INSTi' rather than the keys — hence the uppercase before comparing. Dropping
 * that normalization silently breaks both branches.
 */
export const tradeinTypeLabel = (style: string | null | undefined): string => {
  if (!style) return '—';
  const s = style.toUpperCase();
  if (s === 'INSTI') return 'In-store (INSTI)';
  if (s === 'MITI') return 'Digital Trade-in (DITI)';
  return style;
};

export const isInStore = (tradein: TradeinRecord): boolean =>
  (tradein.tradeinStyle ?? '').toUpperCase() === 'INSTI';

export const TradeinKeyFacts = ({ tradein, states }: { tradein: TradeinRecord, states: ConstantOption[] }) => {
  const currency = tradein.transactionCurrencyCode;
  const facts: KeyFact[] = [
    { label: 'Trade-in Value', value: formatMoney(tradein.tradeinAmount, currency) },
    { label: 'Gift Card', value: formatMoney(tradein.giftcardTotals?.total, currency) },
    { label: 'Items', value: `${tradein.receivedCount ?? 0}/${tradein.initiatedCount ?? 0}` },
    { label: 'Type', value: tradeinTypeLabel(tradein.tradeinStyle) },
    { label: 'Status', value: displayNameFor(states, tradein.state) },
    { label: 'Initiated', value: formatDate(tradein.initiatedAt) },
  ];
  return <KeyFactsStrip facts={facts} />;
};

export const SupplierSection = ({ tradein }: { tradein: TradeinRecord }) => {
  const address = tradein.deliveryAddress;
  return (
    <Box>
      <SectionLabel>Supplier</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <TextField source="deliveryAddress.name" label="Name" emptyText="—" />
        <TextField source="deliveryAddress.email" label="Email" emptyText="—" />
        {address?.phone ? <TextField source="deliveryAddress.phone" label="Phone" /> : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const OriginSection = ({ tradein }: { tradein: TradeinRecord }) => {
  const address = tradein.deliveryAddress;
  const cityState = [address?.city, address?.state].filter(Boolean).join(', ') || null;
  if (isInStore(tradein)) {
    return (
      <Box>
        <SectionLabel>In-Store</SectionLabel>
        <SimpleShowLayout sx={detailLayoutSx}>
          <TextField source="externalRefId" label="Store" emptyText="—" />
          <FunctionField label="City / State" render={() => cityState ?? '—'} />
          <TextField source="countryCode" label="Country" emptyText="—" />
        </SimpleShowLayout>
      </Box>
    );
  }
  return (
    <Box>
      <SectionLabel>Origin</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <TextField source="deliveryAddress.address1" label="Address" emptyText="—" />
        {address?.address2 ? <TextField source="deliveryAddress.address2" label="Address 2" /> : null}
        <FunctionField label="City / State" render={() => cityState ?? '—'} />
        <TextField source="deliveryAddress.postalCode" label="Postal Code" sx={monoFieldSx} emptyText="—" />
        <TextField source="countryCode" label="Country" emptyText="—" />
      </SimpleShowLayout>
    </Box>
  );
};

export const PayoutSection = ({ tradein }: { tradein: TradeinRecord }) => {
  const currency = tradein.transactionCurrencyCode;
  const totals = tradein.giftcardTotals;
  return (
    <Box>
      <SectionLabel>Payout</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <TextField source="paymentPreference" label="Payment Method" emptyText="—" />
        <FunctionField label="Trade-in Value" render={() => formatMoney(tradein.tradeinAmount, currency)} />
        <FunctionField label="Gift Card Total" render={() => formatMoney(totals?.total, currency)} />
        {totals?.refund ? <FunctionField label="— Refund" render={() => formatMoney(totals.refund, currency)} /> : null}
        {totals?.manual ? <FunctionField label="— Manual" render={() => formatMoney(totals.manual, currency)} /> : null}
        {tradein.shippingCostPaidBySupplier != null ? (
          <FunctionField label="Supplier Shipping" render={() => formatMoney(tradein.shippingCostPaidBySupplier, currency)} />
        ) : null}
        {currency && currency !== 'USD' ? <FunctionField label="Currency" render={() => currency} /> : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const ProcessingSection = ({ tradein, sendbackStates }: { tradein: TradeinRecord, sendbackStates: ConstantOption[] }) => (
  <Box>
    <SectionLabel>Processing</SectionLabel>
    <SimpleShowLayout sx={detailLayoutSx}>
      <FunctionField label="Type" render={() => tradeinTypeLabel(tradein.tradeinStyle)} />
      {tradein.sendbackState ? (
        <FunctionField label="Sendback State" render={() => displayNameFor(sendbackStates, tradein.sendbackState)} />
      ) : null}
      <FunctionField label="Sendback Opt-In" render={() => (tradein.isSendbackOptIn ? 'Yes' : 'No')} />
      <FunctionField label="Initiated" render={() => formatDateTime(tradein.initiatedAt)} />
      {tradein.initiatedCompletionAt ? <FunctionField label="Initiated Completion" render={() => formatDateTime(tradein.initiatedCompletionAt)} /> : null}
      {tradein.completedAt ? <FunctionField label="Completed" render={() => formatDateTime(tradein.completedAt)} /> : null}
      {tradein.canceledAt ? <FunctionField label="Canceled" render={() => formatDateTime(tradein.canceledAt)} /> : null}
      {tradein.cohortGroup ? <TextField source="cohortGroup" label="Cohort" /> : null}
      {tradein.externalRefId ? <TextField source="externalRefId" label="External Ref" sx={monoFieldSx} /> : null}
      {tradein.warehouseId != null ? <TextField source="warehouseId" label="Warehouse" /> : null}
    </SimpleShowLayout>
  </Box>
);

/** Trade-in-level flag chips — rendered only when true. */
export const TradeinFlags = ({ tradein }: { tradein: TradeinRecord }) => {
  const needsAuth = present(tradein.tradeinItems).some(i => i.inventoryItem?.needsTroveAuthentication);
  const flags: Flag[] = [
    { label: 'Needs Trove Authentication', color: 'warning', show: needsAuth },
    { label: 'Sendback Opt-In', color: 'default', show: !!tradein.isSendbackOptIn },
    { label: 'Has Escalations', color: 'warning', show: present(tradein.escalations).length > 0 },
    { label: 'In Sendback', color: 'error', show: !!tradein.sendbackState },
    { label: 'In-store', color: 'default', show: isInStore(tradein) },
  ];
  return <FlagChips flags={flags} />;
};
